/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Typography, Box, Stepper, Step, Container, Grid2 } from "@mui/material";
import "./OemActivityLog.css";
import Topheader from "../../../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchApprovalHistoryDetails } from "../../../../Services/redux/Approval-history/ApprovalHistoryActions";
import { fetchPortfolioContentDetails } from "../../../../Services/redux/Application-details/ApplicationDetailsActions";

const formatState = (state) => {
  if (!state) return "";
  return state
    .replaceAll("_", " ")
    .toLowerCase()
    .replaceAll(/\b\w/g, (char) => char.toUpperCase());
};

const getStatusMessage = (stateFrom, stateTo) => {
  const formattedFromState = formatState(stateFrom);
  const formattedToState = formatState(stateTo);
  if (formattedFromState.includes("Approved") || formattedToState.includes("Approved")) {
    return "Your app has been";
  }
  if (
    (formattedFromState.includes("Draft") && formattedToState !== "") ||
    (formattedFromState !== "" && formattedToState.includes("Draft"))
  ) {
    return "Moved from";
  }
  if (formattedFromState.includes("Draft") || formattedToState === "" || formattedFromState === "") {
    return "Saved as";
  }
  return "Moved from";
};

function OemActivityLog() {
  const dispatch = useDispatch();
  const [expandedRows, setExpandedRows] = useState({});

  const { submissionDetails } = useSelector(
    (state) => state.submissionDetails
  );

  const approvalLogs = useSelector(
    (state) => state.approvalHistory.approvalLogs
  );

  const toggleExpand = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
    };
  };

  const getStateColor = (state) => {
    if (state.includes("APPROVED")) return "#d4edda";
    if (state.includes("REJECTED")) return "#f8d7da";
    if (state.includes("DRAFT")) return "#fff3cd";
    return "#e2e3e5";
  };

  const getTextColor = (state) => {
    if (state.includes("APPROVED")) return "#155724";
    if (state.includes("REJECTED")) return "#721c24";
    if (state.includes("DRAFT")) return "#856404";
    return "#383d41";
  };

  useEffect(() => {
    dispatch(
      fetchApprovalHistoryDetails(
        sessionStorage.getItem("portfolioId"),
        sessionStorage.getItem("contentId")
      )
    );
  }, [dispatch]);
  useEffect(() => {
    dispatch(
      fetchPortfolioContentDetails(
        sessionStorage.getItem("portfolioId"),
        sessionStorage.getItem("contentId")
      )
    );
  }, [sessionStorage.getItem("contentId")]);

  return (
    <>
      <Topheader />

      <Container maxWidth="lg">
        <Grid2 container>
          <Grid2 size={12}>
            <div className="container">
              <div className="side-menu" style={{ marginTop: "69px" }}>
                <button
                  className="arrow-back"
                  onClick={() => globalThis.history.back()}
                  onKeyDown={(e) => e.key === "Enter" && globalThis.history.back()}
                  type="button"
                  style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", padding: 0 }}
                >
                  <ArrowBackIcon style={{ color: "#717273" }} />
                  <div className="my-apps">All Apps</div>
                </button>

                <ApplicationDetailsSideMenu submissionData={approvalLogs} />
              </div>

              <div className="actvitylogInfo">
                <Box
                  sx={{
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "28px",
                    marginBottom: "-11px",
                  }}
                >
                  <div
                    className="infoHeader"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {submissionDetails &&
                      Object.keys(submissionDetails).length > 0 && (
                        <div
                          className="app-icon-my-release-activity-log"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <img
                            src={
                              sessionStorage.getItem("customPortal") ===
                              "oem-admin"
                                ? submissionDetails.icon
                                : submissionDetails.icon.url
                            }
                            alt={
                              submissionDetails.fileName
                                ? submissionDetails.fileName
                                : submissionDetails?.descriptions?.en_US?.title
                            }
                            className="submission-header-icon"
                            width={60}
                            height={60}
                            style={{
                              border: "1px solid #FFFFFF66",
                              borderRadius: "4px",
                              marginRight: "10px",
                            }}
                          />
                          <div>
                            <p
                              className="application-name"
                              style={{ margin: 0 }}
                            >
                              {submissionDetails?.descriptions?.en_US?.title}{" "}
                              &nbsp;
                              {submissionDetails?.contentVersion}
                            </p>
                          </div>
                        </div>
                      )}
                  </div>
                </Box>

                <Box
                  sx={{
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "8px",
                  }}
                >
                  <div className="infoHeader">
                    <p className="heading" style={{ margin: 0 }}>
                      Activity Log
                    </p>
                  </div>
                </Box>

                <Box>
                  {approvalLogs.length > 0 ? (
                    <Stepper orientation="vertical" connector={null}>
                      {approvalLogs.map((log, index) => {
                        const { date, time } = formatDateTime(log.eventDate);
                        const cleanComment = decodeURIComponent(
                          log.reason || ""
                        ).trim();
                        const fromState = formatState(log.stateFrom);
                        const toState = formatState(log.stateTo);
                        return (
                          <Step key={`${log.eventDate}-${log.userId}`}>
                            <Box
                              sx={{
                                height: "max-content",
                                width: "100%",
                                display: "flex",
                                alignItems: "flex-start",
                                borderRadius: 2,
                                cursor: log.reason ? "pointer" : "default",
                                backgroundColor: expandedRows[index]
                                  ? "#f9f9f9"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: "#f0f0f0",
                                },
                                padding: "0px 12px 0px 12px",
                              }}
                              onClick={() => log.reason && toggleExpand(index)}
                            >
                              {/* Date and Time */}
                              <Box
                                sx={{ width: "15%", pr: 2, marginTop: "12px" }}
                              >
                                <Typography
                                  className="dateTextActivitylog"
                                >
                                  {date} <br />
                                  <span className="timeText">at {time}</span>
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  mx: 2,
                                  borderLeft: "2px solid #ccc",
                                  height: expandedRows[index]
                                    ? "156px"
                                    : "87px",
                                  position: "relative",
                                }}
                              >
                                <svg
                                  width="13"
                                  height="25"
                                  viewBox="0 0 13 13"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{
                                    marginLeft: "-7.2px",
                                    marginTop: "12px",
                                  }}
                                >
                                  <rect
                                    x="2"
                                    y="2"
                                    width="9"
                                    height="9"
                                    transform="rotate(-45 6.5 6.5)"
                                    fill="#808080"
                                  />
                                </svg>
                              </Box>

                              {/* Main Content */}

                              <Box sx={{ flex: 1, marginTop: "12px" }}>
                                <div
                                  className="acitvity-log-main-sectio-comment"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  {log.userId && (
                                    <div
                                      className="activity-log-user-id"
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: "16px",
                                          marginBottom: "8px",
                                          color: "#000000DE",
                                        }}
                                      >
                                        User ID:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "#555",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        {log.userId}
                                      </Typography>
                                    </div>
                                  )}

                                  {/* Comment icon and arrow */}
                                  {log.reason && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "16px",
                                      }}
                                    >
                                      <svg
                                        width="17"
                                        height="16"
                                        viewBox="0 0 17 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M5.07197 7.2C5.29864 7.2 5.48864 7.12333 5.64197 6.97C5.79531 6.81667 5.87197 6.62667 5.87197 6.4C5.87197 6.17333 5.79531 5.98333 5.64197 5.83C5.48864 5.67667 5.29864 5.6 5.07197 5.6C4.84531 5.6 4.65531 5.67667 4.50197 5.83C4.34864 5.98333 4.27197 6.17333 4.27197 6.4C4.27197 6.62667 4.34864 6.81667 4.50197 6.97C4.65531 7.12333 4.84531 7.2 5.07197 7.2ZM8.27197 7.2C8.49864 7.2 8.68864 7.12333 8.84197 6.97C8.99531 6.81667 9.07197 6.62667 9.07197 6.4C9.07197 6.17333 8.99531 5.98333 8.84197 5.83C8.68864 5.67667 8.49864 5.6 8.27197 5.6C8.04531 5.6 7.85531 5.67667 7.70197 5.83C7.54864 5.98333 7.47197 6.17333 7.47197 6.4C7.47197 6.62667 7.54864 6.81667 7.70197 6.97C7.85531 7.12333 8.04531 7.2 8.27197 7.2ZM11.472 7.2C11.6986 7.2 11.8886 7.12333 12.042 6.97C12.1953 6.81667 12.272 6.62667 12.272 6.4C12.272 6.17333 12.1953 5.98333 12.042 5.83C11.8886 5.67667 11.6986 5.6 11.472 5.6C11.2453 5.6 11.0553 5.67667 10.902 5.83C10.7486 5.98333 10.672 6.17333 10.672 6.4C10.672 6.62667 10.7486 6.81667 10.902 6.97C11.0553 7.12333 11.2453 7.2 11.472 7.2ZM0.271973 16V1.6C0.271973 1.16 0.428639 0.783333 0.741973 0.47C1.05531 0.156667 1.43197 0 1.87197 0H14.672C15.112 0 15.4886 0.156667 15.802 0.47C16.1153 0.783333 16.272 1.16 16.272 1.6V11.2C16.272 11.64 16.1153 12.0167 15.802 12.33C15.4886 12.6433 15.112 12.8 14.672 12.8H3.47197L0.271973 16ZM2.79197 11.2H14.672V1.6H1.87197V12.1L2.79197 11.2Z"
                                          fill="#121415"
                                          fillOpacity="0.6"
                                        />
                                      </svg>

                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{
                                          transform: expandedRows[index]
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                          transition:
                                            "transform 0.2s ease-in-out",
                                        }}
                                      >
                                        <path
                                          d="M7 10L12 15L17 10"
                                          stroke="#555"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </Box>
                                  )}
                                </div>

                                {log.stateFrom && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "center",
                                      mt: 1,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        color: "#5A5B5C",
                                        fontSize: "16px",
                                      }}
                                    >
                                      {getStatusMessage(
                                        log.stateFrom,
                                        log.stateTo
                                      )}
                                    </Typography>
                                    <Box
                                      sx={{
                                        bgcolor: getStateColor(log.stateFrom),
                                        color: getTextColor(log.stateFrom),
                                        padding: "5px 8px",
                                        borderRadius: "5px",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {fromState}
                                    </Box>
                                    {toState && (
                                      <>
                                        <svg
                                          width="17"
                                          height="16"
                                          viewBox="0 0 17 16"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M12.2279 12L10.8029 10.6L12.4029 9H8.22791V7H12.4029L10.8279 5.4L12.2279 4L16.2279 8L12.2279 12ZM1.22791 9C0.944572 9 0.707072 8.90417 0.515405 8.7125C0.323739 8.52083 0.227905 8.28333 0.227905 8C0.227905 7.71667 0.323739 7.47917 0.515405 7.2875C0.707072 7.09583 0.944572 7 1.22791 7C1.51124 7 1.74874 7.09583 1.94041 7.2875C2.13207 7.47917 2.22791 7.71667 2.22791 8C2.22791 8.28333 2.13207 8.52083 1.94041 8.7125C1.74874 8.90417 1.51124 9 1.22791 9ZM5.22791 9C4.94457 9 4.70707 8.90417 4.51541 8.7125C4.32374 8.52083 4.22791 8.28333 4.22791 8C4.22791 7.71667 4.32374 7.47917 4.51541 7.2875C4.70707 7.09583 4.94457 7 5.22791 7C5.51124 7 5.74874 7.09583 5.94041 7.2875C6.13207 7.47917 6.22791 7.71667 6.22791 8C6.22791 8.28333 6.13207 8.52083 5.94041 8.7125C5.74874 8.90417 5.51124 9 5.22791 9Z"
                                            fill="#121415"
                                            fillOpacity="0.6"
                                          />
                                        </svg>

                                        <Box
                                          sx={{
                                            bgcolor: getStateColor(log.stateTo),
                                            color: getTextColor(log.stateTo),
                                            padding: "5px 8px",
                                            borderRadius: "5px",
                                            fontWeight: "600",
                                          }}
                                        >
                                          {toState}
                                        </Box>
                                      </>
                                    )}
                                  </Box>
                                )}
                                {/* Expanded Comment */}
                                {expandedRows[index] && log.reason && (
                                  <Box
                                    sx={{
                                      padding: "12px",
                                      marginTop: "12px",
                                      marginRight: "20px",
                                      position: "relative",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: "2px",
                                        left: 0,
                                        width: "684px",
                                        height: "1px",
                                        backgroundColor: "#ccc",
                                      }}
                                    />
                                    <Typography variant="body2">
                                      <strong>Comment:</strong>{" "}
                                    </Typography>
                                    <Typography>
                                      {cleanComment || "NA"}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Step>
                        );
                      })}
                    </Stepper>
                  ) : (
                    <Typography>No approval history available.</Typography>
                  )}
                </Box>
              </div>
            </div>
          </Grid2>
        </Grid2>
      </Container>
    </>
  );
}

export default OemActivityLog;
